import axios, { AxiosResponse, AxiosInstance, AxiosRequestConfig } from 'axios';
import add from 'date-fns/add';
import isBefore from 'date-fns/isBefore';
import Vue from 'vue';
import parseISO from 'date-fns/parseISO';
import { app } from '../main';
import { authService } from '../msal';

const config = {
    validateStatus(status) {
        return true;
    },
    headers: {}
};

class YoyoHttpService {
    public static getInstance(): YoyoHttpService {
        if (!YoyoHttpService.instance) {
            YoyoHttpService.instance = new YoyoHttpService(axios.create(config));
        }

        return YoyoHttpService.instance;
    }
    private static instance: YoyoHttpService;
    public axiosInstance: AxiosInstance;
    private currentlyExecuting: number = 0;
    private started: boolean = false;
    private timeOut;

    private constructor(axiosInstance: AxiosInstance) {
        this.setInterceptors(axiosInstance);
        this.axiosInstance = axiosInstance;
    }

    public handleErrorMessage(errorMessage, status) {
        if(status == 401 || status == 403) {
            authService.login()
        }
        if (!status.toString().startsWith('2')) {

            if (!errorMessage) {
                errorMessage = app.$t('errors.noShipments');
            }
            
            const errorHeader = app.$t('errors.errorHeader').toString();
            app.$bvToast.toast(errorMessage, {
                title: errorHeader,
                variant: 'danger',
                toaster: 'b-toaster-bottom-full',
                noAutoHide: true
            });
        }
    }

    public async get(url: string, requestConfig, errorMessage?: string) {
        const result = await this.axiosInstance.get(url, requestConfig);
        this.handleErrorMessage(errorMessage, result.status);
        return result;
    }

    public async delete(url: string, requestConfig, errorMessage?: string) {
        const result = await this.axiosInstance.delete(url, requestConfig);
        this.handleErrorMessage(errorMessage, result.status);
        return result;
    }

    public async post(url: string, requestConfig, errorMessage?: string) {
        const result = await this.axiosInstance.post(url, requestConfig);
        this.handleErrorMessage(errorMessage, result.status);
        return result;
    }

    public async cachedGet(url: string, requestConfig, errorMessage?: string): Promise<any> {
        const promise = await new Promise<any>(async (resolve, reject) => {
            const key = this.cachekey(url, requestConfig);
            const cachedItem = this.cachedItem(url, requestConfig);
            if (cachedItem != null) {
                const cachedResponse = {
                    status: 200,
                    statusText: 'OK',
                    data: cachedItem
                };
                resolve(cachedResponse);
            } else {
                const result = await this.axiosInstance.get(url, requestConfig);

                this.handleErrorMessage(errorMessage, result.status);

                this.storeItemWithKey(key, result.data);
                resolve(result);
            }
        });

        return promise;
    }

    public registerCacheScope(scopeId: string) {
        const storedScopeId = localStorage.getItem('app_scopeid');
        if (storedScopeId == null) {
            localStorage.setItem('app_scopeid', scopeId);
        } else {
            if (scopeId !== storedScopeId) {
                localStorage.setItem('app_scopeid', scopeId);
            }
        }
    }
    public clearCachedUrl(url: string, requestConfig) {
        const key = this.cachekey(url, requestConfig);
        localStorage.removeItem(key);
    }

    public storeItem(url: string, requestConfig, data) {
        const validUntill = add(new Date(), { days: 0, minutes: 5 });
        const key = this.cachekey(url, requestConfig);
        const itemToStore = {
            data,
            validUntill
        };
        localStorage.setItem(key, JSON.stringify(itemToStore));
    }

    public storeItemWithKey(key:string, data) {
        const validUntill = add(new Date(), { days: 0, minutes: 5 });
        const itemToStore = {
            data,
            validUntill
        };
        localStorage.setItem(key, JSON.stringify(itemToStore));
    }

    public cachedItem(url: string, requestConfig) {
        const key = this.cachekey(url, requestConfig);
        const itemString = localStorage.getItem(key);
        if (itemString == null) {
            return null;
        }

        const item = JSON.parse(itemString);
        const validUntil = parseISO(item.validUntill);
        const isStillValid = validUntil > new Date();

        if (isStillValid) {
            return item.data; //expired
        }

        return null;
    }

    public cachekey(url: string, requestConfig) {
        if (requestConfig == null) {
            const key = url + '_' + this.axiosInstance.defaults.headers.common["shipmentSystem"];
            return key;
        }

        const key =
            url +
            '_' +
            this.axiosInstance.defaults.headers.common["shipmentSystem"] +
            '_' +
            JSON.stringify(requestConfig);
        return key;
    }

    public setHeader(key: string, value) {
        this.axiosInstance.defaults.headers.common[key] = value;
    }

    public setSignalRConnectionId(connectionId) {
        this.axiosInstance.defaults.headers.post.signalRConnectionId = connectionId;
    }

    private setInterceptors(axiosInstance: AxiosInstance) {
        axiosInstance.interceptors.request.use(async (request) => {
            const token = await authService.acquireTokenSilent();
            request.headers["Log-UserId"] = authService.getUserId();
            request.headers.token = `${token}`;
            
            if(!request.headers.common.shipmentSystem || request.headers.common.shipmentSystem == ''){
                var url = window.location.hostname;
                if(window.location.port != "80")
                    url = url + ":" + window.location.port;
                window.location.replace(window.location.protocol + "//" + url + "/logout");
            }
            
            if (this.currentlyExecuting === 0 && !this.started) {
                window['topProgress'].start();
                this.started = true;
            }
            if (this.timeOut != null) {
                clearTimeout(this.timeOut);
            }
            this.currentlyExecuting++;
            return request;
        });
        axiosInstance.interceptors.response.use(async (response) => {
            this.currentlyExecuting--;

            if (this.timeOut != null) {
                clearTimeout(this.timeOut);
            }
            this.timeOut = setTimeout(() => {
                if (this.currentlyExecuting < 1) {
                    window['topProgress'].finish();
                    this.started = false;
                }
            }, 1500);
            var token = response.headers.token;
            authService.setToken(token);

            return response;
        });
    }
}

export const yoyoHttp = YoyoHttpService.getInstance();
